/**
 * ElementPicker — "Show us where" (design doc Figure 3).
 *
 * Data: unbound (captures from the DOM only; sends nothing itself).
 *
 * HOW IT INTERCEPTS, AND WHY IT IS BUILT THIS WAY (rewritten 2026-09-21 after
 * review: "some items cannot be pointed at"). The first version listened on
 * `document` in the capture phase and swallowed `click`. Two things were wrong
 * with that, and both meant the reporter could not point at exactly the
 * controls most worth reporting:
 *   - Plenty of components act on `mousedown`/`pointerdown`, not `click`.
 *     Swallowing `click` alone let those fire — pointing at "Delete" deleted.
 *   - A `disabled` control emits no pointer events at all, so the one thing a
 *     reporter most wants to report ("this button is greyed out and shouldn't
 *     be") could not be picked.
 *
 * So the picker now owns a full-screen layer with `pointer-events: auto`. It
 * receives every pointer event itself — nothing beneath it ever sees one, on
 * any event type, disabled or not — and resolves the target by hiding itself
 * for one `elementFromPoint` call. Everything on the screen is pickable and
 * nothing on the screen can be actioned. The instruction bar is inside the
 * layer and deliberately `pointer-events: none`, so even the strip it covers
 * stays pickable.
 *
 * WHAT IT RECORDS: control IDENTITY from our own component tree — component
 * name, test id, field key, ARIA role, disabled, and position among the fields
 * of a FORM. That is schema, not content.
 *
 * WHAT IT REFUSES: the value in the control, any text in or around it, the
 * surrounding markup, and which ROW of a list it sits in. "Row 3 of the
 * filtered list" plus the active filter chips can single out an individual —
 * indirect identification with no name attached. A form's field order is
 * schema; a list's row order is data.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { T, alpha } from "@v0y4ge/jrni-ui";
import { useT } from "../lib/i18n";
import type { PickedControl } from "./capture";

interface Box { top: number; left: number; width: number; height: number }

const FIELDISH = "input, select, textarea, [role='combobox'], [role='switch'], [role='checkbox']";

/** A row or list container. Indices are NOT recorded at or above one of these:
 *  a form's field order is schema, a list's row order is data, and "row 3 of
 *  the filtered list" plus the active filters can single out a person. */
const ROWISH = "table, tbody, thead, tr, li, ol, ul, [role='row'], [role='grid'], [role='list'], [role='listitem'], [role='rowgroup']";

/** Attributes safe to record. Author-written schema (`type`, `name`) or live
 *  state (`aria-expanded`), never rendered content. Deliberately EXCLUDES
 *  `aria-label`, `title`, `placeholder`, `alt` and `value` — all five
 *  routinely interpolate a customer's name ("Open menu for Margaret Chen"),
 *  which is exactly the leak this whole feature is built to avoid. */
const SAFE_ATTRS_WITH_VALUES = ["type", "name", "aria-expanded", "aria-haspopup", "aria-checked", "aria-current", "inputmode"];
const SAFE_ATTRS_PRESENCE_ONLY = ["disabled", "required", "readonly", "multiple", "checked", "aria-disabled", "aria-invalid", "aria-required", "contenteditable"];

/** Short, stable, non-cryptographic hash. Its only job is to make two reports
 *  about the same control group together; it is derived from structure alone,
 *  so it can carry nothing about a person. */
function signatureOf(input: string): string {
  let h = 5381;
  for (let i = 0; i < input.length; i++) h = ((h << 5) + h + input.charCodeAt(i)) | 0;
  return "c_" + Math.abs(h).toString(36).padStart(6, "0").slice(0, 8);
}

/** Component names that are STRUCTURE, not identity. `studio-app-root` wraps
 *  the entire application, so it is an ancestor of every control on screen and
 *  discriminates nothing — reporting it as the component (and claiming the
 *  "component" identity tier on the strength of it) was worse than reporting
 *  no component at all, because it asserted a precision the pick never had.
 *  Anything listed here is skipped when building the chain. */
const NON_IDENTIFYING_COMPONENTS = new Set(["studio-app-root"]);

/** Nearest design-system component names, walking up, structural wrappers
 *  excluded. An EMPTY result is meaningful: it means this control has no
 *  design-system ancestry and must be identified structurally instead. */
function componentChain(el: HTMLElement): string[] {
  const chain: string[] = [];
  let node: HTMLElement | null = el;
  while (node && node !== document.body && chain.length < 5) {
    const c = node.getAttribute("data-jrni-component");
    if (c && safeIdentifier(c) && !NON_IDENTIFYING_COMPONENTS.has(c) && chain[0] !== c) chain.unshift(c);
    node = node.parentElement;
  }
  return chain;
}

/**
 * Structural path from body down to the element.
 *
 * Each step is a tag, plus its design-system component name where it has one,
 * plus its position among same-tag siblings. The position is DROPPED for any
 * step at or inside a row/list container — see ROWISH. Nothing else is
 * included: no classes (they can be generated), no ids (they can be record
 * ids), no text of any kind.
 */
function structurePathOf(el: HTMLElement): string {
  const steps: string[] = [];
  let node: HTMLElement | null = el;
  let insideRow = false;
  while (node && node !== document.body && steps.length < 8) {
    const tag = node.tagName.toLowerCase();
    const comp = node.getAttribute("data-jrni-component");
    if (node.matches(ROWISH)) insideRow = true;
    let step = comp && !NON_IDENTIFYING_COMPONENTS.has(comp) ? `${tag}[${comp}]` : tag;
    if (!insideRow) {
      const parent = node.parentElement;
      if (parent) {
        const sameTag = Array.from(parent.children).filter((c) => c.tagName === node!.tagName);
        if (sameTag.length > 1) step += `:${sameTag.indexOf(node) + 1}`;
      }
    }
    steps.unshift(step);
    node = node.parentElement;
  }
  return steps.join(" > ");
}

/**
 * Identify the pointed-at control.
 *
 * EVERY control resolves. The first version reported "this control isn't
 * labelled for support" whenever a test id was missing, which was exactly
 * backwards: an unlabelled control is the one most worth reporting, and the
 * reporter was told their pick was worth less than it was (operator,
 * 2026-09-21 — the QR scanner). Three tiers of identity, in descending
 * strength, and all of them are schema:
 *   1. `testid` — a name we chose for this control.
 *   2. `component` — the design-system component chain.
 *   3. `structure` — the DOM path plus role and safe attributes.
 * The missing test id is still recorded, as a coverage finding for us rather
 * than a degradation for the reporter.
 */
/** A test id (or data-field) is recorded only when it is a pure name: lowercase
 *  kebab with no digits and no uppercase (CC 0082). `appt-row-JRNI-NJE96PE9` or
 *  `appt-payment-7f3a…` carries a record value, so it is dropped and the component
 *  or structure tier identifies the control instead. The next screen that puts a
 *  record in a test id is caught here, not in a report. */
export function safeIdentifier(v: string | null): string | null {
  return v && /^[a-z]+(-[a-z]+)*$/.test(v) ? v : null;
}

function describe(el: HTMLElement): PickedControl {
  const host = (el.closest("[data-testid], [data-jrni-component], button, a, input, select, textarea, [role]") as HTMLElement | null) || el;
  const testid = safeIdentifier(host.getAttribute("data-testid"));
  /* Design-system component names are lowercase kebab ("date-picker",
     "currency-select"), so the same rule as the test id keeps all of them and no
     record value can ride in one (CC 0089 §3, corrected in 0092 §2b). */
  const componentAttr = safeIdentifier(host.getAttribute("data-jrni-component"));
  const explicitRole = host.getAttribute("role");
  const tag = host.tagName.toLowerCase();
  const inputType = tag === "input" ? (host.getAttribute("type") || "text") : null;
  const role = explicitRole
    || (tag === "button" ? "button" : tag === "a" ? "link" : tag === "select" ? "combobox" : inputType ? `input:${inputType}` : tag);
  const region = host.closest("form, [data-jrni-region], section") as HTMLElement | null;
  const inList = !!host.closest("table, [role='row'], [role='grid'], [role='listitem'], li, tr");
  let fieldPosition: PickedControl["fieldPosition"] = null;
  if (region && !inList) {
    const fields = Array.from(region.querySelectorAll<HTMLElement>(FIELDISH));
    const idx = fields.indexOf(host);
    if (idx >= 0) fieldPosition = { index: idx + 1, total: fields.length };
  }
  const fieldAttr = safeIdentifier(host.getAttribute("data-field"));
  const chain = componentChain(host);
  const structurePath = structurePathOf(host);
  const attrs: string[] = [];
  for (const a of SAFE_ATTRS_WITH_VALUES) {
    // `name` is author text and could carry a value: kept only as a pure kebab name.
    const v = a === "name" ? safeIdentifier(host.getAttribute(a)) : host.getAttribute(a);
    if (v !== null && v !== "") attrs.push(`${a}=${v}`);
  }
  for (const a of SAFE_ATTRS_PRESENCE_ONLY) if (host.hasAttribute(a)) attrs.push(a);
  /* Presence of a label is useful; its TEXT is not ours to take. */
  if (host.hasAttribute("aria-label") || host.hasAttribute("aria-labelledby")) attrs.push("has-accessible-name");
  const svgIcon = host.querySelector("svg");
  if (svgIcon) attrs.push("contains-icon");

  /* The three tiers, resolved from what the element ACTUALLY carries. A pick
     only claims the "component" tier when a real design-system ancestor was
     found; otherwise the structural path is both the identity and the
     headline, and the tier says so. */
  const ownComponent = componentAttr && !NON_IDENTIFYING_COMPONENTS.has(componentAttr) ? componentAttr : null;
  const identity: PickedControl["identity"] = testid ? "testid" : (ownComponent || chain.length) ? "component" : "structure";
  const component = ownComponent
    || chain[chain.length - 1]
    || (testid ? testid.replace(/[-_]([a-z])/g, (_m, c: string) => c.toUpperCase()) : tag);

  return {
    component,
    componentPath: chain.length ? chain.join(" > ") : structurePath,
    testid,
    field: fieldAttr || (testid ? testid.replace(/-(input|select|field|control|btn|button)$/, "") : null),
    role,
    disabled: host.hasAttribute("disabled") || host.getAttribute("aria-disabled") === "true",
    fieldPosition,
    identity,
    structurePath,
    signature: signatureOf(structurePath + "|" + role),
    attrs,
    labelled: !!testid,
  };
}

export function ElementPicker({ onPick, onCancel }: { onPick: (c: PickedControl) => void; onCancel: () => void }) {
  const t = useT();
  const [box, setBox] = useState<Box | null>(null);
  const layerRef = useRef<HTMLDivElement>(null);

  /** Hit-test beneath our own layer. One hide/restore per call; nothing else
   *  in the document is touched, so no screen can react to being probed.
   *
   *  It disables POINTER EVENTS rather than hiding the layer: a
   *  `visibility: hidden` element is still returned by `elementFromPoint` in
   *  this engine, so the first version resolved every pick to the picker's own
   *  layer (`div` / `role=dialog`) and recorded itself. */
  const beneath = useCallback((x: number, y: number): HTMLElement | null => {
    const layer = layerRef.current;
    if (!layer) return null;
    const prev = layer.style.pointerEvents;
    layer.style.pointerEvents = "none";
    const el = document.elementFromPoint(x, y) as HTMLElement | null;
    layer.style.pointerEvents = prev;
    /* May be an SVG <path> inside a button — `describe()` walks up to the
       nearest identifiable host, so that resolves correctly. */
    return el;
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); onCancel(); } };
    document.addEventListener("keydown", onKey, true);
    return () => document.removeEventListener("keydown", onKey, true);
  }, [onCancel]);

  const track = (e: React.PointerEvent | React.MouseEvent) => {
    const el = beneath(e.clientX, e.clientY);
    if (!el) { setBox(null); return; }
    const r = el.getBoundingClientRect();
    setBox({ top: r.top, left: r.left, width: r.width, height: r.height });
  };

  const pick = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const el = beneath(e.clientX, e.clientY);
    if (el && !el.closest("[data-support-picker-chrome]")) onPick(describe(el));
  };

  const swallow = (e: React.SyntheticEvent) => { e.preventDefault(); e.stopPropagation(); };

  return (
    <div
      ref={layerRef}
      data-support-picker-chrome
      role="dialog"
      aria-label={t("support.picker.instruction", "Click the control that's wrong · Esc to cancel")}
      onPointerMove={track}
      onPointerDown={pick}
      /* Everything else is swallowed so the page beneath cannot act on any of
         it — the point of the picker is that pointing is never pressing. */
      onPointerUp={swallow}
      onClick={swallow}
      onDoubleClick={swallow}
      onContextMenu={swallow}
      onMouseDown={swallow}
      onMouseUp={swallow}
      style={{ position: "fixed", inset: 0, zIndex: 4000, cursor: "crosshair", background: "transparent" }}
    >
      {box ? (
        <div style={{
          position: "fixed", top: box.top, left: box.left, width: box.width, height: box.height,
          border: `2px solid ${T.red1}`, borderRadius: 6, background: alpha(T.red1, 8),
          boxShadow: `0 0 0 4px ${alpha(T.red1, 22)}`, pointerEvents: "none", transition: "all 60ms linear",
        }} />
      ) : null}
      {/* ui-raw: instruction band pinned over the app while picking. RED, not
          neutral (operator, 2026-09-21): this is a MODE — every click on the
          screen means something different while it is up — and a mode needs a
          colour the user cannot mistake for ordinary chrome. `pointer-events:
          none` so the strip it covers stays pickable. */}
      <div style={{
        position: "fixed", top: 0, left: 0, right: 0, background: T.red1, color: T.onColor,
        padding: "10px 16px", fontSize: 13, fontWeight: 500, display: "flex", alignItems: "center",
        justifyContent: "space-between", gap: 12, flexWrap: "wrap", pointerEvents: "none",
        boxShadow: `0 2px 8px ${alpha(T.red1, 35)}`,
      }}>
        <span>{t("support.picker.instruction", "Click the control that's wrong · Esc to cancel")}</span>
        <span style={{ opacity: 0.85, fontWeight: 400 }}>{t("support.picker.nothingYet", "nothing is captured until you click · buttons will not activate")}</span>
      </div>
    </div>
  );
}
